import { Button, Center, Container, Group, NativeSelect, Stack, Text } from '@mantine/core';
import { createFileRoute, LoaderFnContext, useRouter } from '@tanstack/react-router';
import { PageHeader } from '@/components/PageHeader';
import { MyRouterContext } from '@/App';
import { MainContainer } from '@/components/MainContainer';
import { PendingInitialize } from '@/components/PendingInitialize';
import { ProjectMemberList } from './-components/ProjectMemberList';
import { useCallback } from 'react';
import { notifyError, notifySuccess } from '@/lib/notification';
import { logger } from '@/lib/logger';
import { modals } from '@mantine/modals';
import { useForm } from 'react-hook-form';
import { IconUserPlus } from '@tabler/icons-react';
import { ProjectMember, User } from '@/clients/types';
import { isPersonalTenant } from '@/lib/auth';

type ProjectRole = ProjectMember['project_role'];

export const Route = createFileRoute('/_main/_project/project/members/')({
  loader,
  component: Component,
  pendingComponent: () => <PendingInitialize title="プロジェクトメンバー" />,
});

async function loader({
  context: { adminClient, projectId, userInfo },
}: LoaderFnContext<{ projectId: string }, { project: string | undefined }, MyRouterContext>) {
  let projectMembers = null;
  let users = null;
  try {
    if (projectId) {
      projectMembers = await adminClient
        .settings_v1_get_project_members({
          params: { project_id: projectId },
        })
        .then((res) => res.items);
    }
    users = await adminClient.settings_v1_get_users().then((res) => res.items);
  } catch (error) {
    logger.warn(error);
  }

  return {
    adminClient,
    projectMembers,
    projectId,
    users: users ?? [],
    userInfo,
  };
}

function Component() {
  const { adminClient, projectMembers, projectId, users, userInfo } = Route.useLoaderData();
  const { project } = Route.parentRoute.useLoaderData();
  const router = useRouter();
  const isArchived = project?.status === 'archived';
  const isOwner = project?.role === 'owner';
  const readonly = isArchived || !isOwner;

  const addMember = useCallback(
    async (user: User, role: ProjectRole) => {
      try {
        await adminClient.settings_v1_add_project_member(
          {
            user_id: user.user_id,
            role,
          },
          {
            params: { project_id: projectId! },
          }
        );
        await router.load();
        notifySuccess(`${user.email} をプロジェクトに追加しました。`);
        modals.closeAll();
      } catch (error) {
        logger.error(error);
        notifyError(`${user.email} のプロジェクトへの追加に失敗しました。`);
      }
    },
    [adminClient, router, projectId]
  );

  const openAddMemberModal = (users: User[]) => {
    modals.open({
      size: 'md',
      title: 'プロジェクトメンバー追加',
      children: <AddMemberForm onSubmit={addMember} users={users} />,
    });
  };

  return (
    <MainContainer>
      <PageHeader
        title="プロジェクトメンバー"
        description="プロジェクトを利用できるメンバーの管理を行います。"
      />
      {isArchived && <Text c="red">このプロジェクトはアーカイブされています。</Text>}
      {!projectId && <Text>右上のプロジェクト選択から、プロジェクトを選択してください。</Text>}
      {projectId && !project && (
        <Text>
          プロジェクトが見つかりませんでした。
          <br />
          右上のプロジェクト選択から、プロジェクトを選択してください。
        </Text>
      )}
      {projectId && project && project.allow_all_users && (
        <Text>
          このプロジェクトは、全ユーザが「プロジェクト管理者」として利用できます。
          <br />
          メンバーを限定したい場合は、新しいプロジェクトを作成してください。
        </Text>
      )}
      {projectId && project && !project.allow_all_users && projectMembers && (
        <>
          {!isPersonalTenant(userInfo) && (
            <Group justify="end">
              <Button
                color="blue"
                size="sm"
                leftSection={<IconUserPlus style={{ width: '70%', height: '70%' }} />}
                onClick={() => {
                  openAddMemberModal(
                    users.filter((u) => !projectMembers.some((m) => m.user_id === u.user_id))
                  );
                }}
                disabled={readonly}
              >
                メンバーを追加する
              </Button>
            </Group>
          )}
          <ProjectMemberList
            adminClient={adminClient}
            members={projectMembers}
            projectId={projectId}
            readonly={readonly}
            userInfo={userInfo}
          />
        </>
      )}
    </MainContainer>
  );
}

type Inputs = {
  email: string;
  role: ProjectRole;
};

type FormProps = {
  onSubmit: (user: User, role: ProjectRole) => void;
  users: User[];
};

function AddMemberForm({ onSubmit: onAddMember, users }: FormProps) {
  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<Inputs>({
    mode: 'onChange',
    defaultValues: {
      email: '',
      role: 'owner',
    },
  });

  const userItems = users.map((user) => ({
    label: user.email,
    value: user.user_id,
  }));
  const roleItems = [
    { label: 'プロジェクト管理者', value: 'owner' },
    { label: 'メンバー', value: 'member' },
  ];

  const onSubmit = (data: Inputs) => {
    const user = users.find((u) => u.user_id === data.email);
    if (!user) {
      notifyError(`ユーザが見つかりません。 ${data.email}`);
      return;
    }
    onAddMember(user, data.role);
  };

  return (
    <Container m="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Center>
          <Stack w="100%">
            <NativeSelect
              data={userItems}
              label="新規メンバー"
              {...register('email', {
                required: '追加したいメンバーを選択してください。',
              })}
              error={errors.email?.message}
            />
            <NativeSelect
              data={roleItems}
              label="権限"
              {...register('role', {
                required: '権限を選択してください。',
              })}
              error={errors.role?.message}
            ></NativeSelect>
            {/* 表にすると狭いモーダルで 320px 幅を要求して説明文が切れるので、ただの一覧にする */}
            <Stack gap="xs">
              <Text size="xs">
                <b>プロジェクト管理者</b> — プロジェクトの API
                キー管理とメンバーの追加・削除ができます。
              </Text>
              <Text size="xs">
                <b>メンバー</b> — プロジェクトの情報の閲覧だけができます。
              </Text>
            </Stack>
            <Button type="submit" size="sm" disabled={!isValid} mt="lg">
              追加
            </Button>
            <Text size="xs">
              ※
              プロジェクトメンバーに追加するには、対象のユーザがテナントに所属している必要があります。
              <br />※ テナント管理者は、プロジェクト管理者としてアクセスができるため追加不要です。
            </Text>
          </Stack>
        </Center>
      </form>
    </Container>
  );
}
